import type { LayerState, MapNodeSettings, TileField } from "@fortex/api";

export type CatalogueRoot = "maps" | "layers";

export type CatalogueKind = "group" | "tile" | "special" | "folder" | "geoobject";

export type CataloguePen = { color: string; width: number; type: number | null; border: string | null };

export type CatalogueBrush = { color: string; style: number | null; opacity: number };

export type CatalogueIcon = {
  url: string;
  selectedUrl: string | null;
  hoveredUrl: string | null;
  width: number;
  height: number;
  anchor: [number, number];
  growth: number;
};

export type CatalogueStyle = {
  pen: CataloguePen | null;
  brush: CatalogueBrush | null;
  icon: CatalogueIcon | null;
};

export type CatalogueTile = {
  url: string;
  subdomains: string;
  attribution: string;
  minZoom: number;
  maxZoom: number;
  maxNativeZoom: number;
  tileSize: number;
  detectRetina: boolean;
  dark: boolean;
  thumbnail: string | null;
};

export type CatalogueSpecial = {
  provider: "google" | "bing" | string;
  variant: string | null;
  apiKey: string | null;
  options: Record<string, string>;
  attribution: string;
  dark: boolean;
  thumbnail: string | null;
};

export type CatalogueCounts = { point: number; line: number; polygon: number; total: number };

export type CatalogueBounds = { minLat: number; minLon: number; maxLat: number; maxLon: number };

export type CatalogueSource = {
  folder: string | null;
  layer: string | null;
  fields: Record<string, TileField[]>;
  typeIdName: string | null;
  typeIdNames: string[];
  counts: CatalogueCounts | null;
  bounds: CatalogueBounds | null;
  hasTable: boolean;
};

export type CatalogueTiles = { tileUrlTemplate: string; minZoom: number; maxZoom: number };

export type CatalogueVector = { node: string; simplify: boolean; maxFeatures: number; cluster: boolean };

export type LegendEntry = { label: string; color?: string | null; fillColor?: string | null; iconUrl?: string | null; shape?: "point" | "line" | "polygon" };

export type CatalogueProblem = { key: string; problem: string };

export type CatalogueEntry = {
  id: number;
  idName: string;
  key: string;
  root: CatalogueRoot;
  kind: CatalogueKind;
  name: string;
  order: number;
  visible: boolean;
  parentKey: string | null;
  path: string[];
  pathNames: string[];
  depth: number;
  selection: "single" | "multi";
  children: CatalogueEntry[];
  collapsed: boolean;
  defaultSelected: boolean;
  opacity: number;
  minZoom: number | null;
  maxZoom: number | null;
  attribution: string | null;
  bounds: CatalogueBounds | null;
  style: CatalogueStyle | null;
  tile: CatalogueTile | null;
  special: CatalogueSpecial | null;
  source: CatalogueSource | null;
  layerState: LayerState | null;
  tiles: CatalogueTiles | null;
  vector: CatalogueVector | null;
  legend: LegendEntry[] | null;
  settings: MapNodeSettings;
  rawValue: string | null;
  problems: string[];
  origin: "server" | "host";
};

export type Catalogue = {
  langId: number;
  maps: CatalogueEntry[];
  layers: CatalogueEntry[];
  entries: CatalogueEntry[];
  byKey: Map<string, CatalogueEntry>;
  problems: CatalogueProblem[];
  origin: "server" | "host";
};

export const layerIdPrefix = {
  maps: "map",
  layers: "layer",
  custom: "custom",
  track: "track"
} as const;

export function catalogueKey(root: string, key: string): string {
  return `${root}:${key}`;
}

export function catalogueEntryOf(catalogue: Catalogue | null, snapshot: { root: string; key: string }): CatalogueEntry | null {
  return catalogue?.byKey.get(catalogueKey(snapshot.root, snapshot.key)) ?? null;
}

export function layerId(root: CatalogueRoot | "custom" | "track", key: string): string {
  const prefix = root === "maps" ? layerIdPrefix.maps : root === "layers" ? layerIdPrefix.layers : root;

  return `${prefix}:${key}`;
}

export function isLeaf(entry: CatalogueEntry): boolean {
  return entry.kind !== "group";
}

export function* walk(entries: CatalogueEntry[]): Generator<CatalogueEntry> {
  for (const entry of entries) {
    yield entry;
    yield* walk(entry.children);
  }
}

export function leavesOf(entry: CatalogueEntry): CatalogueEntry[] {
  if (isLeaf(entry)) return [entry];

  return entry.children.flatMap(leavesOf);
}
